import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import UploadIcon from '@mui/icons-material/Upload'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined'
import { font, ink, line, radius, surface } from 'features/dashboard/tokens'
import File from 'features/bpDetails/Components/File'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import ProcessingFilesPill from 'features/bpDetails/Components/files/ProcessingFilesPill'
import EstateFolderFormModal from 'features/bpList/Components/estates/EstateFolderFormModal'
import EstateFolderDeleteModal from 'features/bpList/Components/estates/EstateFolderDeleteModal'
import EstateMoveFileModal from 'features/bpList/Components/estates/EstateMoveFileModal'
import EstateFileRenameModal from 'features/bpList/Components/estates/EstateFileRenameModal'
import EstateFileDeleteModal from 'features/bpList/Components/estates/EstateFileDeleteModal'
import EstateBulkMoveFilesModal from 'features/bpList/Components/estates/EstateBulkMoveFilesModal'
import EstateBulkDeleteFilesModal from 'features/bpList/Components/estates/EstateBulkDeleteFilesModal'
import EstateShareFileModal from 'features/bpList/Components/estates/EstateShareFileModal'
import EstateFileBuildingSharesModal from 'features/bpList/Components/estates/EstateFileBuildingSharesModal'
import BulkShareEstateFilesToBpsModal from 'features/bpList/Components/estates/BulkShareEstateFilesToBpsModal'
import ManageEstateFolderPermissions from 'features/bpList/Components/estates/ManageEstateFolderPermissions'
import EstateFileBuildingSharesChip from 'features/bpList/Components/estates/EstateFileBuildingSharesChip'
import useMarqueeSelection from 'hooks/useMarqueeSelection'
import { getSelectionRange, isTextEditingTarget } from 'utils/fileMultiSelection'
import {
  TreeNavigationPanel,
  TreeHeader,
  FolderTree,
  TreeFolder,
  FolderHeader,
  FolderIconStyled,
  FolderNameWithCount,
  ExpandIcon,
  CategoryCount
} from 'features/bpDetails/Components/UnifiedFiles'
import {
  loadEstateFoldersRoutine,
  loadEstateOwnedFilesRoutine,
  uploadEstateDocumentRoutine,
  getEstateFolderUserCountsRoutine
} from 'ducks/estateDocuments/actions'
import {
  selectEstateFolders,
  selectIsLoadingEstateFolders,
  selectEstateOwnedFiles,
  selectEstateOwnedFilesMeta,
  selectEstateOwnedFilesFolderId,
  selectIsLoadingEstateOwnedFiles,
  selectIsUploadingEstateDocument,
  selectEstateFolderUserCounts
} from 'ducks/estateDocuments/selectors'
import { getFileTypes } from 'ducks/files/selectors'
import { getFileTypeName } from 'utils/bpData'

const CYAN = '#33becc'
const ALL_DOCUMENTS_ID = 'all'
const ALL_DOCUMENTS_FOLDER = { id: ALL_DOCUMENTS_ID, name: 'All documents' }

const getFolderFileCount = folder => {
  const count = folder?.fileCount
  return typeof count === 'number' ? count : null
}

const EstateFolderNode = ({
  folder,
  depth,
  selectedFolderId,
  expandedIds,
  onSelect,
  onToggleExpand,
  canEdit,
  onNewSubfolder,
  onRename,
  onDelete,
  onManageAccess,
  userCounts
}) => {
  const hasChildren = Array.isArray(folder.children) && folder.children.length > 0
  const isExpanded = expandedIds.has(folder.id)
  const isSelected = selectedFolderId === folder.id
  const count = getFolderFileCount(folder)
  const userCount = userCounts[folder.id] || 0
  const [menuAnchor, setMenuAnchor] = useState(null)
  const menuOpen = Boolean(menuAnchor)

  const handleClick = () => {
    onSelect(folder)
    if (hasChildren) onToggleExpand(folder.id)
  }

  const openMenu = event => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
  }

  const closeMenu = event => {
    event?.stopPropagation?.()
    setMenuAnchor(null)
  }

  const runMenuAction = handler => event => {
    event?.stopPropagation?.()
    setMenuAnchor(null)
    handler(folder)
  }

  return (
    <TreeFolder>
      <FolderHeader
        isSelected={isSelected}
        folderColor={CYAN}
        onClick={handleClick}
        style={{ paddingLeft: 2 + depth * 16 }}
      >
        <ExpandIcon>
          {hasChildren && (isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />)}
        </ExpandIcon>
        <FolderIconStyled color={CYAN}>
          {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
        </FolderIconStyled>
        <FolderNameWithCount>
          <span>{folder.name}</span>
          {userCount > 0 && (
            <FolderLockBadge title={`Restricted to ${userCount} member${userCount === 1 ? '' : 's'}`}>
              <LockPersonOutlinedIcon style={{ fontSize: 12 }} />
              {userCount}
            </FolderLockBadge>
          )}
          {count !== null && <CategoryCount folderColor={CYAN}>{count}</CategoryCount>}
        </FolderNameWithCount>
        {canEdit && (
          <FolderMenuTrigger
            type='button'
            aria-label={`Folder actions for ${folder.name}`}
            aria-haspopup='menu'
            aria-expanded={menuOpen}
            onClick={openMenu}
          >
            <MoreVertIcon style={{ fontSize: 16 }} />
          </FolderMenuTrigger>
        )}
      </FolderHeader>

      {canEdit && (
        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={closeMenu}
          onClick={event => event.stopPropagation()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={runMenuAction(onNewSubfolder)}>
            <ListItemIcon><CreateNewFolderOutlinedIcon fontSize='small' /></ListItemIcon>
            <ListItemText>New subfolder</ListItemText>
          </MenuItem>
          <MenuItem onClick={runMenuAction(onRename)}>
            <ListItemIcon><EditOutlinedIcon fontSize='small' /></ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
          <MenuItem onClick={runMenuAction(onManageAccess)}>
            <ListItemIcon><LockPersonOutlinedIcon fontSize='small' /></ListItemIcon>
            <ListItemText>Manage permissions</ListItemText>
          </MenuItem>
          <MenuItem onClick={runMenuAction(onDelete)} sx={{ color: '#b00020' }}>
            <ListItemIcon><DeleteOutlineIcon fontSize='small' sx={{ color: '#b00020' }} /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      )}

      {isExpanded && hasChildren && folder.children.map(child => (
        <EstateFolderNode
          key={child.id}
          folder={child}
          depth={depth + 1}
          selectedFolderId={selectedFolderId}
          expandedIds={expandedIds}
          onSelect={onSelect}
          onToggleExpand={onToggleExpand}
          canEdit={canEdit}
          onNewSubfolder={onNewSubfolder}
          onRename={onRename}
          onDelete={onDelete}
          onManageAccess={onManageAccess}
          userCounts={userCounts}
        />
      ))}
    </TreeFolder>
  )
}

const EstateOwnedFolderView = ({ estate, buildings = [] }) => {
  const estateId = estate?.id
  const dispatch = useDispatch()
  const folders = useSelector(selectEstateFolders)
  const isLoadingFolders = useSelector(selectIsLoadingEstateFolders)
  const files = useSelector(selectEstateOwnedFiles)
  const meta = useSelector(selectEstateOwnedFilesMeta)
  const loadedFolderId = useSelector(selectEstateOwnedFilesFolderId)
  const isLoadingFiles = useSelector(selectIsLoadingEstateOwnedFiles)
  const isUploading = useSelector(selectIsUploadingEstateDocument)
  const fileTypes = useSelector(getFileTypes)
  const folderUserCounts = useSelector(selectEstateFolderUserCounts)
  const canEdit = Boolean(estate?.canEdit)

  const [selectedFolder, setSelectedFolder] = useState(ALL_DOCUMENTS_FOLDER)
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [folderModal, setFolderModal] = useState(null)
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null)
  const [accessFolderTarget, setAccessFolderTarget] = useState(null)
  const [moveFileTarget, setMoveFileTarget] = useState(null)
  const [renameFileTarget, setRenameFileTarget] = useState(null)
  const [deleteFileTarget, setDeleteFileTarget] = useState(null)
  const [shareFileTarget, setShareFileTarget] = useState(null)
  const [bpSharesFileTarget, setBpSharesFileTarget] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkShareOpen, setBulkShareOpen] = useState(false)
  const selectionAnchorRef = useRef(null)
  const fileInputRef = useRef(null)
  const gridPanelRef = useRef(null)

  useEffect(() => {
    if (!estateId) return
    dispatch(loadEstateFoldersRoutine({ estateId }))
    dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId: ALL_DOCUMENTS_ID, page: 1 }))
    if (canEdit) dispatch(getEstateFolderUserCountsRoutine.trigger({ estateId }))
  }, [estateId, dispatch, canEdit])

  useEffect(() => {
    setSelectedIds([])
    selectionAnchorRef.current = null
  }, [estateId, selectedFolder.id])

  const selectFolder = useCallback(folder => {
    if (folder.id === selectedFolder.id) return
    setSelectedFolder({ id: folder.id, name: folder.name })
    dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId: folder.id, page: 1 }))
  }, [dispatch, estateId, selectedFolder.id])

  const toggleExpand = useCallback(folderId => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }, [])

  const handleLoadMore = useCallback(() => {
    const nextPage = Math.floor(files.length / 30) + 1
    dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId: selectedFolder.id, page: nextPage }))
  }, [dispatch, estateId, selectedFolder.id, files.length])

  const handleUploadClick = () => fileInputRef.current?.click()

  const uploadFiles = fileList => {
    const filesToUpload = Array.from(fileList || [])
    filesToUpload.forEach(file => dispatch(uploadEstateDocumentRoutine({ estateId, file, folderId: selectedFolder.id })))
  }

  const handleFilesSelected = event => {
    uploadFiles(event.target.files)
    event.target.value = ''
  }

  const selectableIds = files.map(file => file.id)
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id))

  const toggleFileSelection = useCallback((fileId, isChecked, event) => {
    if (event?.shiftKey && selectionAnchorRef.current) {
      const rangeIds = getSelectionRange(selectableIds, selectionAnchorRef.current, fileId)
      if (rangeIds.length > 0) {
        setSelectedIds(rangeIds)
        return
      }
    }

    selectionAnchorRef.current = fileId
    setSelectedIds(prev => (isChecked ? [...prev, fileId] : prev.filter(id => id !== fileId)))
  }, [selectableIds])

  useEffect(() => {
    setSelectedIds(prev => {
      const pruned = prev.filter(id => selectableIds.includes(id))
      return pruned.length === prev.length ? prev : pruned
    })
  }, [selectableIds])

  useEffect(() => {
    const handleSelectAllShortcut = event => {
      if (!canEdit) return
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'a') return
      if (isTextEditingTarget(event.target)) return

      event.preventDefault()
      setSelectedIds(selectableIds)
      selectionAnchorRef.current = selectableIds[0] || null
    }

    document.addEventListener('keydown', handleSelectAllShortcut)
    return () => document.removeEventListener('keydown', handleSelectAllShortcut)
  }, [selectableIds, canEdit])

  const { isDragSelecting, selectionRect, handleMouseDown, handleContentClick } = useMarqueeSelection({
    containerRef: gridPanelRef,
    selectedIds,
    onChange: setSelectedIds,
    disabled: !canEdit
  })

  const openCreateFolder = (parentFolder = null) => {
    setFolderModal({
      mode: 'create',
      folder: null,
      parentFolderId: parentFolder?.id ?? null,
      parentFolderName: parentFolder?.name ?? null
    })
  }

  const openRenameFolder = folder => {
    setFolderModal({ mode: 'rename', folder, parentFolderId: null, parentFolderName: null })
  }

  const closeFolderModal = () => setFolderModal(null)

  const closeDeleteFolder = () => setDeleteFolderTarget(null)

  const handleFolderDeleted = deletedFolderId => {
    if (selectedFolder.id === deletedFolderId) {
      setSelectedFolder(ALL_DOCUMENTS_FOLDER)
      dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId: ALL_DOCUMENTS_ID, page: 1 }))
    } else {
      dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId: selectedFolder.id, page: 1 }))
    }
  }

  const closeAccessFolder = () => setAccessFolderTarget(null)

  const closeMoveFile = () => setMoveFileTarget(null)

  const closeRenameFile = () => setRenameFileTarget(null)

  const closeDeleteFile = () => setDeleteFileTarget(null)

  const closeShareFile = () => setShareFileTarget(null)

  const closeBpSharesModal = () => setBpSharesFileTarget(null)

  const closeBulkMove = () => setBulkMoveOpen(false)

  const closeBulkDelete = () => setBulkDeleteOpen(false)

  const closeBulkShare = () => setBulkShareOpen(false)

  const clearSelection = () => setSelectedIds([])

  const refreshOwnedFiles = () => dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId: selectedFolder.id, page: 1, reloadLoaded: true }))

  const refreshAfterProcessing = () => {
    refreshOwnedFiles()
    dispatch(loadEstateFoldersRoutine({ estateId }))
  }

  if (!estateId) return null

  const hasMore = meta.currentPage < meta.lastPage
  const showSkeleton = loadedFolderId !== selectedFolder.id
  const showEmpty = !showSkeleton && !isLoadingFiles && files.length === 0
  const showAllDocumentsCount = selectedFolder.id === ALL_DOCUMENTS_ID && meta.total > 0

  return (
    <Wrap>
      <ActionsRow>
        {canEdit && (
          <UploadButton type='button' onClick={handleUploadClick} disabled={isUploading}>
            <UploadIcon sx={{ fontSize: 18 }} />
            {isUploading ? 'Uploading…' : 'Upload'}
          </UploadButton>
        )}
        <HiddenInput ref={fileInputRef} type='file' multiple onChange={handleFilesSelected} />
      </ActionsRow>

      <ProcessingFilesPill type='estate' id={estateId} onCleared={refreshAfterProcessing} />

      <OwnedLayout>
        <RailPanel>
          <TreeHeader>
            <span>Folders</span>
            {canEdit && (
              <NewFolderButton
                type='button'
                onClick={() => openCreateFolder(selectedFolder.id !== ALL_DOCUMENTS_ID ? selectedFolder : null)}
              >
                <AddRoundedIcon style={{ fontSize: 16 }} />
                New folder
              </NewFolderButton>
            )}
          </TreeHeader>
          <FolderTree>
            <TreeFolder>
              <FolderHeader
                isSelected={selectedFolder.id === ALL_DOCUMENTS_ID}
                folderColor={CYAN}
                onClick={() => selectFolder(ALL_DOCUMENTS_FOLDER)}
              >
                <ExpandIcon />
                <FolderIconStyled color={CYAN}>
                  {selectedFolder.id === ALL_DOCUMENTS_ID ? <FolderOpenIcon /> : <FolderIcon />}
                </FolderIconStyled>
                <FolderNameWithCount>
                  <span>All documents</span>
                  {showAllDocumentsCount && <CategoryCount folderColor={CYAN}>{meta.total}</CategoryCount>}
                </FolderNameWithCount>
              </FolderHeader>
            </TreeFolder>

            {isLoadingFolders && folders.length === 0 && (
              <FolderSkeletonRows>
                <SkeletonLine $w='70%' />
                <SkeletonLine $w='50%' />
                <SkeletonLine $w='60%' />
              </FolderSkeletonRows>
            )}

            {folders.map(folder => (
              <EstateFolderNode
                key={folder.id}
                folder={folder}
                depth={0}
                selectedFolderId={selectedFolder.id}
                expandedIds={expandedIds}
                onSelect={selectFolder}
                onToggleExpand={toggleExpand}
                canEdit={canEdit}
                onNewSubfolder={openCreateFolder}
                onRename={openRenameFolder}
                onDelete={setDeleteFolderTarget}
                onManageAccess={setAccessFolderTarget}
                userCounts={folderUserCounts}
              />
            ))}
          </FolderTree>
        </RailPanel>

        <GridPanel
          ref={gridPanelRef}
          onMouseDown={handleMouseDown}
          onClick={handleContentClick}
        >
          {isDragSelecting && selectionRect && <SelectionOverlay style={selectionRect} />}

          <GridHeader>
            <GridTitle>{selectedFolder.name}</GridTitle>
            {!showSkeleton && meta.total > 0 && (
              <GridCount>
                Showing {files.length.toLocaleString('en-GB')} of {meta.total.toLocaleString('en-GB')}
              </GridCount>
            )}
          </GridHeader>

          {canEdit && selectedIds.length > 0 && (
            <BulkBar>
              <BulkCount>{selectedIds.length} selected</BulkCount>
              <BulkButton type='button' onClick={() => setBulkMoveOpen(true)}>
                <DriveFileMoveOutlinedIcon style={{ fontSize: 16 }} />
                Move
              </BulkButton>
              {buildings.length > 0 && (
                <BulkButton type='button' onClick={() => setBulkShareOpen(true)}>
                  <IosShareOutlinedIcon style={{ fontSize: 16 }} />
                  Share to buildings
                </BulkButton>
              )}
              <BulkDeleteButton type='button' onClick={() => setBulkDeleteOpen(true)}>
                <DeleteOutlineIcon style={{ fontSize: 16 }} />
                Delete
              </BulkDeleteButton>
              <BulkSpacer />
              <BulkTextLink type='button' onClick={() => setSelectedIds(allSelected ? [] : selectableIds)}>
                {allSelected ? 'Deselect all' : 'Select all'}
              </BulkTextLink>
              <BulkTextLink type='button' onClick={clearSelection}>Clear</BulkTextLink>
            </BulkBar>
          )}

          {showSkeleton ? (
            <FileGrid>
              {Array.from({ length: 8 }).map((_, index) => <SkeletonCard key={index} />)}
            </FileGrid>
          ) : showEmpty ? (
            <EmptyState>
              <EmptyTitle>No documents in this folder yet</EmptyTitle>
              <EmptyBody>Upload your first one to get started.</EmptyBody>
              {canEdit && (
                <UploadButton type='button' onClick={handleUploadClick} disabled={isUploading}>
                  <UploadIcon sx={{ fontSize: 18 }} />
                  {isUploading ? 'Uploading…' : 'Upload a document'}
                </UploadButton>
              )}
            </EmptyState>
          ) : (
            <>
              <FileGrid>
                {files.map(file => {
                  const shareCount = file.buildingShareCount || file.building_share_count || 0
                  return (
                    <OwnedFileCard key={file.id} className='file-card' data-file-id={file.id}>
                      <File
                        file={file}
                        type={getFileTypeName(file.fileType?.id || '', fileTypes)}
                        viewMode='grid'
                        showThumbnail
                        estateId={estateId}
                        readOnlyAggregate={!canEdit}
                        hideBulkSelect={!canEdit}
                        isSelected={selectedIds.includes(file.id)}
                        onSelect={toggleFileSelection}
                      />
                      {shareCount > 0 && (
                        <EstateFileBuildingSharesChip
                          count={shareCount}
                          onClick={() => setBpSharesFileTarget(file)}
                        />
                      )}
                      {canEdit && (
                        <FileActionsRow>
                          <FileActionButton type='button' onClick={() => setRenameFileTarget(file)}>
                            <EditOutlinedIcon style={{ fontSize: 16 }} />
                            Edit details
                          </FileActionButton>
                          <FileActionButton type='button' onClick={() => setShareFileTarget(file)}>
                            <IosShareOutlinedIcon style={{ fontSize: 16 }} />
                            Share
                          </FileActionButton>
                          <FileActionButton type='button' onClick={() => setMoveFileTarget(file)}>
                            <DriveFileMoveOutlinedIcon style={{ fontSize: 16 }} />
                            Move
                          </FileActionButton>
                          <DeleteFileButton type='button' onClick={() => setDeleteFileTarget(file)}>
                            <DeleteOutlineIcon style={{ fontSize: 16 }} />
                            Delete
                          </DeleteFileButton>
                        </FileActionsRow>
                      )}
                    </OwnedFileCard>
                  )
                })}
              </FileGrid>
              <InfiniteScrollSentinel
                hasMore={hasMore}
                loading={isLoadingFiles}
                onLoadMore={handleLoadMore}
                root={gridPanelRef.current}
              />
            </>
          )}
        </GridPanel>
      </OwnedLayout>

      {folderModal && (
        <EstateFolderFormModal
          estateId={estateId}
          mode={folderModal.mode}
          folder={folderModal.folder}
          parentFolderId={folderModal.parentFolderId}
          parentFolderName={folderModal.parentFolderName}
          onClose={closeFolderModal}
        />
      )}

      {deleteFolderTarget && (
        <EstateFolderDeleteModal
          estateId={estateId}
          folder={deleteFolderTarget}
          onClose={closeDeleteFolder}
          onDeleted={handleFolderDeleted}
        />
      )}

      {accessFolderTarget && (
        <ManageEstateFolderPermissions
          estateId={estateId}
          folder={accessFolderTarget}
          onClose={closeAccessFolder}
        />
      )}

      {moveFileTarget && (
        <EstateMoveFileModal
          estateId={estateId}
          file={moveFileTarget}
          folders={folders}
          sourceFolderId={selectedFolder.id !== ALL_DOCUMENTS_ID ? selectedFolder.id : null}
          onClose={closeMoveFile}
        />
      )}

      {renameFileTarget && (
        <EstateFileRenameModal
          estateId={estateId}
          file={renameFileTarget}
          folderId={selectedFolder.id}
          onClose={closeRenameFile}
        />
      )}

      {deleteFileTarget && (
        <EstateFileDeleteModal
          estateId={estateId}
          file={deleteFileTarget}
          folderId={selectedFolder.id}
          onClose={closeDeleteFile}
        />
      )}

      {shareFileTarget && (
        <EstateShareFileModal
          estateId={estateId}
          file={shareFileTarget}
          buildingPassports={buildings}
          onChanged={refreshOwnedFiles}
          onClose={closeShareFile}
        />
      )}

      {bpSharesFileTarget && (
        <EstateFileBuildingSharesModal
          estateId={estateId}
          file={bpSharesFileTarget}
          canEdit={canEdit}
          onChanged={refreshOwnedFiles}
          onClose={closeBpSharesModal}
        />
      )}

      {bulkMoveOpen && (
        <EstateBulkMoveFilesModal
          estateId={estateId}
          fileIds={selectedIds}
          folders={folders}
          sourceFolderId={selectedFolder.id !== ALL_DOCUMENTS_ID ? selectedFolder.id : null}
          onClose={closeBulkMove}
          onMoved={clearSelection}
        />
      )}

      {bulkDeleteOpen && (
        <EstateBulkDeleteFilesModal
          estateId={estateId}
          fileIds={selectedIds}
          folderId={selectedFolder.id}
          onClose={closeBulkDelete}
          onDeleted={clearSelection}
        />
      )}

      {bulkShareOpen && (
        <BulkShareEstateFilesToBpsModal
          estateId={estateId}
          fileIds={selectedIds}
          buildingPassports={buildings}
          onShared={() => { clearSelection(); refreshOwnedFiles() }}
          onClose={closeBulkShare}
        />
      )}
    </Wrap>
  )
}

export default EstateOwnedFolderView

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
`

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const HiddenInput = styled.input`
  display: none;
`

const UploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: ${radius.sm};
  background: ${CYAN};
  color: #ffffff;
  font-size: ${font.emphasis};
  font-weight: 600;
  cursor: pointer;
  transition: background 160ms ease;

  &:hover:not(:disabled) {
    background: #2aa6b3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const NewFolderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 40px;
  margin-left: auto;
  padding: 0 12px;
  border: 1px solid rgba(51, 190, 204, 0.4);
  border-radius: ${radius.sm};
  background: #ffffff;
  color: #2aa6b3;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 160ms ease, border-color 160ms ease;

  &:hover {
    background: #f2fbfb;
    border-color: ${CYAN};
  }

  &:focus-visible {
    outline: 2px solid ${CYAN};
    outline-offset: 1px;
  }
`

const FolderMenuTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: auto;
  flex-shrink: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${ink.mute};
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 120ms ease, background 120ms ease, color 120ms ease;

  ${FolderHeader}:hover &,
  &:hover,
  &:focus-visible {
    opacity: 1;
    background: rgba(12, 18, 41, 0.06);
    color: ${ink.base};
  }

  &:focus-visible {
    outline: 2px solid ${CYAN};
    outline-offset: 1px;
  }
`

const FolderLockBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
  padding: 1px 7px;
  border-radius: ${radius.pill};
  background: rgba(12, 18, 41, 0.06);
  color: ${ink.mute};
  font-size: 10.5px;
  font-weight: 700;
`

const OwnedLayout = styled.div`
  display: flex;
  gap: 20px;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    overflow: visible;
  }
`

const RailPanel = styled(TreeNavigationPanel)`
  @media (max-width: 768px) {
    display: flex !important;
    width: 100%;
    max-height: 280px;
  }
`

const GridPanel = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  padding: 20px;
  overflow-y: auto;
  user-select: none;

  @media (max-width: 768px) {
    padding: 14px;
    border-radius: ${radius.sm};
  }
`

const SelectionOverlay = styled.div`
  position: absolute;
  border: 1px solid ${CYAN};
  background: rgba(51, 190, 204, 0.12);
  border-radius: 2px;
  pointer-events: none;
  z-index: 5;
`

const GridHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const GridTitle = styled.h4`
  margin: 0;
  font-size: ${font.emphasis};
  font-weight: 600;
  color: ${ink.base};
`

const GridCount = styled.span`
  font-size: ${font.small};
  color: ${ink.mute};
`

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: ${radius.sm};
  background: rgba(51, 190, 204, 0.06);
  border: 1px solid rgba(51, 190, 204, 0.25);
  flex-wrap: wrap;
`

const BulkCount = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: #2aa6b3;
`

const BulkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.small};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 160ms ease, color 160ms ease, background 160ms ease;

  &:hover {
    border-color: ${CYAN};
    color: #2aa6b3;
    background: #f2fbfb;
  }

  &:focus-visible {
    outline: 2px solid ${CYAN};
    outline-offset: 1px;
  }
`

const BulkDeleteButton = styled(BulkButton)`
  border-color: rgba(176, 0, 32, 0.35);
  color: #b00020;

  &:hover {
    border-color: #b00020;
    color: #b00020;
    background: #fdecea;
  }

  &:focus-visible {
    outline: 2px solid #b00020;
  }
`

const BulkSpacer = styled.span`
  flex: 1;
`

const BulkTextLink = styled.button`
  border: none;
  background: none;
  padding: 4px 2px;
  cursor: pointer;
  font-size: ${font.small};
  font-weight: 600;
  color: #2aa6b3;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${CYAN};
    outline-offset: 1px;
  }
`

const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const OwnedFileCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const FileActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`

const FileActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1 1 auto;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.small};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 160ms ease, color 160ms ease, background 160ms ease;

  &:hover {
    border-color: ${CYAN};
    color: #2aa6b3;
    background: #f2fbfb;
  }

  &:focus-visible {
    outline: 2px solid ${CYAN};
    outline-offset: 1px;
  }
`

const DeleteFileButton = styled(FileActionButton)`
  border-color: rgba(176, 0, 32, 0.35);
  color: #b00020;

  &:hover {
    border-color: #b00020;
    color: #b00020;
    background: #fdecea;
  }

  &:focus-visible {
    outline: 2px solid #b00020;
  }
`

const SkeletonCard = styled.div`
  height: 168px;
  border-radius: ${radius.md};
  background: #e7e9ee;
  animation: ${skeletonPulse} 1.3s ease-in-out infinite;
`

const FolderSkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 4px;
`

const SkeletonLine = styled.span`
  display: block;
  height: 11px;
  width: ${({ $w }) => $w || '100%'};
  border-radius: ${radius.xs};
  background: #e7e9ee;
  animation: ${skeletonPulse} 1.3s ease-in-out infinite;
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  text-align: center;
`

const EmptyTitle = styled.p`
  margin: 0;
  font-size: ${font.emphasis};
  font-weight: 600;
  color: ${ink.base};
`

const EmptyBody = styled.p`
  margin: 0 0 6px 0;
  font-size: ${font.body};
  color: ${ink.mute};
`
